"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ToolbarFocusable = exports.ToolbarFocusableComponent = void 0;
const tslib_1 = require("tslib");
const styled_1 = tslib_1.__importDefault(require("@emotion/styled"));
const React = tslib_1.__importStar(require("react"));
const styled_tools_1 = require("styled-tools");
const Box_1 = require("./Box");
const Toolbar_1 = require("./Toolbar");
const utils_1 = require("./utils");
const { forwardRef, useState, useCallback, useLayoutEffect, useRef } = React;
exports.ToolbarFocusableComponent = forwardRef((_a, refIn) => {
    var { disabled, tabIndex: propTabIndex = -1, onFocus } = _a, props = tslib_1.__rest(_a, ["disabled", "tabIndex", "onFocus"]);
    const myRef = useRef(null);
    const ref = refIn === null ? myRef : refIn;
    const toolbarRef = useRef(null);
    const firstRender = useRef(true);
    const [tabIndex, setTabIndex] = useState(propTabIndex);
    const getToolbar = useCallback(() => {
        if (toolbarRef.current === null && ref.current !== null) {
            toolbarRef.current = ref.current.closest(utils_1.getSelector(Toolbar_1.Toolbar));
        }
        return toolbarRef.current;
    }, [ref, toolbarRef]);
    const getFocusables = useCallback(() => {
        const toolbar = getToolbar();
        if (toolbar === null) {
            return undefined;
        }
        return toolbar.querySelectorAll(utils_1.getSelector(exports.ToolbarFocusable));
    }, [getToolbar]);
    const getCurrentIndex = useCallback((focusables) => {
        if (focusables === undefined) {
            return -1;
        }
        let currentIndex = -1;
        focusables.forEach((item, i) => {
            if (item === ref.current) {
                currentIndex = i;
            }
        });
        return currentIndex;
    }, [ref]);
    const toolbarIsVertical = useCallback(() => {
        const toolbar = getToolbar();
        return toolbar !== null && toolbar.getAttribute('aria-orientation') === 'vertical';
    }, [getToolbar]);
    const getPreviousFocusable = useCallback((focusables, currentIndex) => {
        const index = currentIndex ? currentIndex - 1 : focusables.length - 1;
        return focusables.item(index);
    }, []);
    const getNextFocusable = useCallback((focusables, currentIndex) => {
        const index = currentIndex + 1;
        return focusables.item(index) || focusables.item(0);
    }, []);
    const handleKeyDown = useCallback((e) => {
        const isVertical = toolbarIsVertical();
        const nextKey = isVertical ? 'ArrowDown' : 'ArrowRight';
        const previousKey = isVertical ? 'ArrowUp' : 'ArrowLeft';
        const willPerformEvent = [nextKey, previousKey].indexOf(e.key) >= 0;
        if (willPerformEvent) {
            const focusables = getFocusables();
            if (focusables !== undefined) {
                const currentIndex = getCurrentIndex(focusables);
                e.preventDefault();
                setTabIndex(propTabIndex);
                if (e.key === nextKey) {
                    getNextFocusable(focusables, currentIndex).focus();
                }
                else {
                    getPreviousFocusable(focusables, currentIndex).focus();
                }
            }
        }
    }, [toolbarIsVertical, getFocusables, getCurrentIndex, propTabIndex, getNextFocusable, getPreviousFocusable]);
    const handleFocus = useCallback(() => setTabIndex(0), [setTabIndex]);
    useLayoutEffect(() => {
        if (firstRender.current && tabIndex === -1) {
            firstRender.current = false;
            setTabIndex(getCurrentIndex(getFocusables()));
        }
        if (!disabled && ref.current !== null) {
            ref.current.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            if (ref.current !== null) {
                ref.current.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [tabIndex, disabled, setTabIndex, getCurrentIndex, getFocusables, ref]);
    return React.createElement(Box_1.Box, Object.assign({}, props, { ref: ref, onFocus: utils_1.callAll(handleFocus, onFocus), tabIndex: tabIndex }));
});
exports.ToolbarFocusable = styled_1.default(exports.ToolbarFocusableComponent) `
  ${styled_tools_1.theme('ToolbarFocusable')};
`;

//# sourceMappingURL=ToolbarFocusable.js.map
